import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { BusinessOpportunity, OpportunitySubscriptionPlan } from '@/types/business-opportunities';
import { useAuthContext } from '@/contexts/AuthContext';
import { getBusinessOpportunity, createBusinessOpportunity, updateBusinessOpportunity, addOpportunitySubscriptionPlan } from '@/services/business-opportunities';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFieldArray } from 'react-hook-form';
import * as z from 'zod';
import { CalendarIcon } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from '@/lib/utils';

// Form validation schema
const formSchema = z.object({
  name: z.string().min(3, 'O nome deve ter pelo menos 3 caracteres'),
  customer_id: z.string().min(1, 'Selecione um cliente'),
  description: z.string().optional(),
  probability: z.coerce.number().min(0).max(100),
  currency_code: z.string().default('BRL'),
  total_value: z.coerce.number().min(0).optional(),
  expected_close_date: z.date().optional(),
  status: z.enum(['open', 'won', 'lost', 'canceled']).default('open'),
  subscription_plans: z.array(
    z.object({
      subscription_plan_id: z.string().min(1, 'Selecione um plano'),
      quantity: z.coerce.number().min(1, 'Quantidade mínima é 1'),
      unit_price: z.coerce.number().min(0, 'Preço deve ser maior ou igual a 0'),
      discount_percentage: z.coerce.number().min(0).max(100).default(0),
    })
  ).optional(),
});

type FormValues = z.infer<typeof formSchema>;

const BusinessOpportunityForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const auth = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [customers, setCustomers] = useState<{ id: string; name: string }[]>([]);
  const [initialData, setInitialData] = useState<BusinessOpportunity | null>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<{ id: string; name: string; price: number; description?: string; }[]>([]);
  const [initialPlans, setInitialPlans] = useState<OpportunitySubscriptionPlan[]>([]);
  
  // Extrair userId de auth
  const userId = auth.user?.id;
  
  const isEditMode = !!id;
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      customer_id: '',
      description: '',
      probability: 10,
      currency_code: 'BRL',
      total_value: undefined,
      expected_close_date: undefined,
      status: 'open',
      subscription_plans: [],
    },
  });

  // Setup field array for subscription plans
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "subscription_plans",
  });

  // Watch for customer_id changes to enable subscription plans section
  const customerIdValue = form.watch('customer_id');
  
  // Calculate total value based on subscription plans
  const calculateTotalValue = () => {
    const plans = form.getValues('subscription_plans') || [];
    return plans.reduce((total, plan) => {
      const quantity = plan.quantity || 0;
      const unitPrice = plan.unit_price || 0;
      const discount = plan.discount_percentage || 0;
      return total + (quantity * unitPrice * (1 - discount / 100));
    }, 0);
  };

  // Update total value when subscription plans change
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name?.startsWith('subscription_plans')) {
        const totalValue = calculateTotalValue();
        form.setValue('total_value', totalValue);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form, form.watch]);

  // Fetch customers for dropdown
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        console.log('Fetching customers');
        
        const { data, error } = await supabase
          .from('customers')
          .select('id, name')
          .order('name');
        
        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }
        
        console.log('Customers loaded:', data?.length || 0, 'records');
        setCustomers(data || []);
      } catch (error) {
        console.error('Error fetching customers:', error);
        toast({
          title: 'Erro ao carregar clientes',
          description: 'Não foi possível carregar a lista de clientes.',
          variant: 'destructive',
        });
      }
    };

    fetchCustomers();
  }, [toast]);

  // Fetch subscription plans when customer is selected
  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      if (!customerIdValue) return;
      
      try {
        // First get the subscription plans
        const { data: plansData, error: plansError } = await supabase
          .from('subscription_plans')
          .select('id, name, description')
          .eq('is_active', true)
          .order('name');
        
        if (plansError) throw plansError;
        
        if (!plansData || plansData.length === 0) {
          setSubscriptionPlans([]);
          return;
        }
        
        // Get the plan IDs to fetch prices
        const planIds = plansData.map(plan => plan.id);
        
        // Get the currency from the form
        const currency = form.getValues('currency_code') || 'BRL';
        
        // Fetch prices for these plans in the selected currency
        const { data: pricesData, error: pricesError } = await supabase
          .from('subscription_plan_prices')
          .select('subscription_plan_id, price_amount, currency')
          .in('subscription_plan_id', planIds)
          .eq('currency', currency);
          
        if (pricesError) throw pricesError;
        
        // Map prices to plans
        const plansWithPrices = plansData.map(plan => {
          // Find price for this plan in the selected currency
          const planPrice = pricesData?.find(
            price => price.subscription_plan_id === plan.id && price.currency === currency
          );
          
          return {
            ...plan,
            price: planPrice?.price_amount || 0,
          };
        });
        
        setSubscriptionPlans(plansWithPrices);
      } catch (error) {
        console.error('Error fetching subscription plans:', error);
        toast({
          title: 'Erro ao carregar planos',
          description: 'Não foi possível carregar a lista de planos de assinatura.',
          variant: 'destructive',
        });
      }
    };

    fetchSubscriptionPlans();
  }, [customerIdValue, toast, form]);

  // Also update when currency changes to get prices in the new currency
  useEffect(() => {
    if (customerIdValue) {
      const subscription = form.watch((value, { name }) => {
        if (name === 'currency_code') {
          // Refetch plans with prices in the new currency
          const fetchSubscriptionPlans = async () => {
            try {
              // First get the subscription plans
              const { data: plansData, error: plansError } = await supabase
                .from('subscription_plans')
                .select('id, name, description')
                .eq('is_active', true)
                .order('name');
              
              if (plansError) throw plansError;
              
              if (!plansData || plansData.length === 0) {
                setSubscriptionPlans([]);
                return;
              }
              
              // Get the plan IDs to fetch prices
              const planIds = plansData.map(plan => plan.id);
              
              // Get the new currency from the form
              const currency = value.currency_code || 'BRL';
              
              // Fetch prices for these plans in the selected currency
              const { data: pricesData, error: pricesError } = await supabase
                .from('subscription_plan_prices')
                .select('subscription_plan_id, price_amount, currency')
                .in('subscription_plan_id', planIds)
                .eq('currency', currency);
                
              if (pricesError) throw pricesError;
              
              // Map prices to plans
              const plansWithPrices = plansData.map(plan => {
                // Find price for this plan in the selected currency
                const planPrice = pricesData?.find(
                  price => price.subscription_plan_id === plan.id && price.currency === currency
                );
                
                return {
                  ...plan,
                  price: planPrice?.price_amount || 0,
                };
              });
              
              setSubscriptionPlans(plansWithPrices);
              
              // Update prices in the current subscription plan fields
              const currentPlans = form.getValues('subscription_plans') || [];
              currentPlans.forEach((plan, index) => {
                const planWithPrice = plansWithPrices.find(p => p.id === plan.subscription_plan_id);
                if (planWithPrice) {
                  form.setValue(`subscription_plans.${index}.unit_price`, planWithPrice.price);
                }
              });
            } catch (error) {
              console.error('Error fetching subscription plans:', error);
              toast({
                title: 'Erro ao carregar planos',
                description: 'Não foi possível carregar a lista de planos de assinatura.',
                variant: 'destructive',
              });
            }
          };
          
          fetchSubscriptionPlans();
        }
      });
      
      return () => subscription.unsubscribe();
    }
  }, [customerIdValue, form]);

  // Fetch opportunity data if in edit mode
  useEffect(() => {
    const fetchOpportunityData = async () => {
      if (!isEditMode) return;
      
      try {
        setIsLoading(true);
        const opportunityData = await getBusinessOpportunity(id);
        setInitialData(opportunityData);
        
        // Set form values
        form.reset({
          name: opportunityData.name,
          customer_id: opportunityData.customer_id,
          description: opportunityData.description || '',
          probability: opportunityData.probability,
          currency_code: opportunityData.currency_code,
          total_value: opportunityData.total_value,
          expected_close_date: opportunityData.expected_close_date ? new Date(opportunityData.expected_close_date) : undefined,
          status: opportunityData.status,
          subscription_plans: opportunityData.opportunity_subscription_plans?.map(plan => ({
            subscription_plan_id: plan.subscription_plan_id,
            quantity: plan.quantity,
            unit_price: plan.unit_price,
            discount_percentage: plan.discount_percentage,
          })) || [],
        });
        
        if (opportunityData.opportunity_subscription_plans) {
          setInitialPlans(opportunityData.opportunity_subscription_plans);
        }
      } catch (error) {
        console.error('Error fetching opportunity data:', error);
        toast({
          title: 'Erro ao carregar oportunidade',
          description: 'Não foi possível carregar os dados da oportunidade para edição.',
          variant: 'destructive',
        });
        navigate('/business-opportunities');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOpportunityData();
  }, [id, isEditMode, form, toast, navigate]);

  // Handle add subscription plan
  const handleAddPlan = () => {
    append({
      subscription_plan_id: '',
      quantity: 1,
      unit_price: 0,
      discount_percentage: 0,
    });
  };

  // Handle plan selection
  const handlePlanSelection = (index: number, planId: string) => {
    const selectedPlan = subscriptionPlans.find(plan => plan.id === planId);
    if (selectedPlan) {
      form.setValue(`subscription_plans.${index}.unit_price`, selectedPlan.price || 0);
    }
  };

  const onSubmit = async (data: FormValues) => {
    if (!userId) {
      toast({
        title: 'Erro de autenticação',
        description: 'Você precisa estar autenticado para realizar esta ação.',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setIsLoading(true);
      console.log('Form data:', data);
      
      const opportunityData = {
        ...data,
        updated_by: userId,
        created_by: isEditMode ? undefined : userId,
        // Converter Date para string ISO
        expected_close_date: data.expected_close_date ? data.expected_close_date.toISOString() : undefined,
      };
      
      // Remove subscription_plans from opportunityData as they are stored separately
      const { subscription_plans, ...restOpportunityData } = opportunityData;
      
      console.log('Creating business opportunity with data:', restOpportunityData);
      
      let opportunityId = id;
      
      if (isEditMode && id) {
        // Update existing opportunity
        const updatedOpportunity = await updateBusinessOpportunity(id, restOpportunityData);
        console.log('Updated opportunity:', updatedOpportunity);
        toast({
          title: 'Oportunidade atualizada',
          description: 'A oportunidade foi atualizada com sucesso.',
        });
        opportunityId = updatedOpportunity.id;
      } else {
        // Create new opportunity
        const newOpportunity = await createBusinessOpportunity(restOpportunityData);
        console.log('Created opportunity:', newOpportunity);
        toast({
          title: 'Oportunidade criada',
          description: 'A oportunidade foi criada com sucesso.',
        });
        opportunityId = newOpportunity.id;
      }
      
      // Save subscription plans if any
      if (subscription_plans && subscription_plans.length > 0 && opportunityId) {
        console.log('Adding subscription plans:', subscription_plans);
        
        for (const plan of subscription_plans) {
          if (!plan.subscription_plan_id) {
            console.warn('Skipping plan with no subscription_plan_id');
            continue;
          }
          
          try {
            // Keep only the essential fields for subscription plans
            const planData = {
              opportunity_id: opportunityId,
              subscription_plan_id: plan.subscription_plan_id,
              quantity: plan.quantity || 1,
              unit_price: plan.unit_price || 0,
              discount_percentage: plan.discount_percentage || 0,
              total_price: (plan.quantity || 1) * (plan.unit_price || 0) * (1 - (plan.discount_percentage || 0) / 100)
            };
            
            console.log('Adding plan:', planData);
            const addedPlan = await addOpportunitySubscriptionPlan(planData);
            console.log('Added plan:', addedPlan);
          } catch (error) {
            console.error('Error adding subscription plan:', error);
            // Continue with other plans even if one fails
          }
        }
      }
      
      navigate(`/business-opportunities/${opportunityId}`);
    } catch (error) {
      console.error('Error saving opportunity:', error);
      toast({
        title: `Erro ao ${isEditMode ? 'atualizar' : 'criar'} oportunidade`,
        description: `Não foi possível ${isEditMode ? 'atualizar' : 'criar'} a oportunidade de negócio.`,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (isEditMode && id) {
      navigate(`/business-opportunities/${id}`);
    } else {
      navigate('/business-opportunities');
    }
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon" onClick={handleCancel}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-extrabold">
              {isEditMode ? 'Editar Oportunidade' : 'Nova Oportunidade'}
            </h1>
            <p className="text-muted-foreground">
              {isEditMode
                ? 'Atualize os dados da oportunidade de negócio'
                : 'Crie uma nova oportunidade de negócio'}
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dados da Oportunidade</CardTitle>
          <CardDescription>
            Preencha as informações da oportunidade de negócio
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome da Oportunidade *</FormLabel>
                      <FormControl>
                        <Input placeholder="Nome da oportunidade" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="customer_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Cliente *</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione um cliente" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {customers.map((customer) => (
                            <SelectItem key={customer.id} value={customer.id}>
                              {customer.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Descreva a oportunidade de negócio" 
                        className="min-h-[100px]" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid md:grid-cols-3 gap-6">
                <FormField
                  control={form.control}
                  name="probability"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Probabilidade de Fechamento (%)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          min={0} 
                          max={100} 
                          placeholder="10" 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="currency_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Moeda</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a moeda" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="BRL">Real (BRL)</SelectItem>
                          <SelectItem value="USD">Dólar (USD)</SelectItem>
                          <SelectItem value="EUR">Euro (EUR)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="total_value"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valor Total</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          min={0} 
                          step="0.01" 
                          placeholder="0.00" 
                          disabled={!!(fields.length > 0)}
                          {...field} 
                        />
                      </FormControl>
                      <FormDescription>
                        {fields.length > 0 ? 'Calculado automaticamente com base nos planos' : ''}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="expected_close_date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Data Esperada de Fechamento</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full pl-3 text-left font-extrabold",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PP", { locale: ptBR })
                              ) : (
                                <span>Selecione a data</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            disabled={(date) =>
                              date < new Date()
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="open">Aberta</SelectItem>
                        <SelectItem value="won">Ganha</SelectItem>
                        <SelectItem value="lost">Perdida</SelectItem>
                        <SelectItem value="canceled">Cancelada</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Subscription Plans Section */}
              {customerIdValue && (
                <div className="mt-8">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-lg font-extrabold">Planos de Assinatura</h3>
                      <p className="text-sm text-muted-foreground">
                        Adicione planos de assinatura a esta oportunidade
                      </p>
                    </div>
                    <Button type="button" onClick={handleAddPlan} variant="outline">
                      <Plus className="mr-2 h-4 w-4" />
                      Adicionar Plano
                    </Button>
                  </div>
                  
                  {fields.length === 0 ? (
                    <div className="text-center p-4 border rounded-md bg-muted/50">
                      <p className="text-muted-foreground">
                        Nenhum plano adicionado. Clique em "Adicionar Plano" para incluir planos de assinatura.
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Plano</TableHead>
                            <TableHead>Quantidade</TableHead>
                            <TableHead>Preço Unitário</TableHead>
                            <TableHead>Desconto (%)</TableHead>
                            <TableHead>Total</TableHead>
                            <TableHead className="w-[80px]">Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {fields.map((field, index) => {
                            const unitPrice = form.watch(`subscription_plans.${index}.unit_price`) || 0;
                            const quantity = form.watch(`subscription_plans.${index}.quantity`) || 0;
                            const discount = form.watch(`subscription_plans.${index}.discount_percentage`) || 0;
                            const total = quantity * unitPrice * (1 - discount / 100);
                            const currency = form.watch('currency_code') || 'BRL';
                            
                            return (
                              <TableRow key={field.id}>
                                <TableCell>
                                  <FormField
                                    control={form.control}
                                    name={`subscription_plans.${index}.subscription_plan_id`}
                                    render={({ field }) => (
                                      <Select 
                                        onValueChange={(value) => {
                                          field.onChange(value);
                                          handlePlanSelection(index, value);
                                        }} 
                                        value={field.value}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Selecione um plano" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {subscriptionPlans.map((plan) => (
                                            <SelectItem key={plan.id} value={plan.id}>
                                              {plan.name}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    )}
                                  />
                                </TableCell>
                                <TableCell>
                                  <FormField
                                    control={form.control}
                                    name={`subscription_plans.${index}.quantity`}
                                    render={({ field }) => (
                                      <Input 
                                        type="number" 
                                        min={1} 
                                        className="w-20"
                                        {...field} 
                                      />
                                    )}
                                  />
                                </TableCell>
                                <TableCell>
                                  <FormField
                                    control={form.control}
                                    name={`subscription_plans.${index}.unit_price`}
                                    render={({ field }) => (
                                      <Input 
                                        type="number" 
                                        min={0} 
                                        step="0.01"
                                        className="w-28"
                                        {...field} 
                                      />
                                    )}
                                  />
                                </TableCell>
                                <TableCell>
                                  <FormField
                                    control={form.control}
                                    name={`subscription_plans.${index}.discount_percentage`}
                                    render={({ field }) => (
                                      <Input 
                                        type="number" 
                                        min={0} 
                                        max={100}
                                        className="w-20"
                                        {...field} 
                                      />
                                    )}
                                  />
                                </TableCell>
                                <TableCell className="font-extrabold">
                                  {formatCurrency(total, currency)}
                                </TableCell>
                                <TableCell>
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    className="h-8 w-8 p-0" 
                                    onClick={() => remove(index)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex justify-end space-x-2">
                <Button variant="outline" type="button" onClick={handleCancel}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <div className="animate-spin mr-2 h-4 w-4 border-2 border-b-transparent rounded-full"></div>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Salvar
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessOpportunityForm; 